import React, { useState } from "react";
import { FaDownload, FaFileCode, FaFileCsv, FaCheck, FaCloudUploadAlt, FaExclamationCircle } from "react-icons/fa";

const exampleJson = {
    variable1: "valeur1",
    variable2: "valeur2",
    variable3: 123
};

const exampleCsv = `variable1,variable2,variable3
valeur1,valeur2,123
`;

const MAX_IMPORT_FILE_SIZE = 10 * 1024 * 1024;
const ACCEPTED_IMPORT_EXTENSIONS = [".json", ".csv"];
const ACCEPTED_IMPORT_MIME_TYPES = ["application/json", "text/csv"];
const UNSUPPORTED_FORMAT_MESSAGE = "Format non supporté.\nVeuillez importer un fichier CSV ou JSON.";
const FILE_TOO_LARGE_MESSAGE = "Fichier trop volumineux.\nVeuillez importer un fichier de 10 MB maximum.";

const isAcceptedImportFile = (file) => {
    const fileName = file.name.toLowerCase();
    const hasAcceptedExtension = ACCEPTED_IMPORT_EXTENSIONS.some(extension => fileName.endsWith(extension));
    const hasAcceptedMimeType = ACCEPTED_IMPORT_MIME_TYPES.includes(file.type);

    return hasAcceptedExtension || hasAcceptedMimeType;
};

const Import = () => {
    const [fileName, setFileName] = useState(null);
    const [success, setSuccess] = useState(false);
    const [dragOver, setDragOver] = useState(false);
    const [error, setError] = useState(null);

    const resetSelection = () => {
        setFileName(null);
        setSuccess(false);
    };

    const validateAndSelectFile = (file) => {
        if (!file) return;

        if (!isAcceptedImportFile(file)) {
            resetSelection();
            setError(UNSUPPORTED_FORMAT_MESSAGE);
            return;
        }

        if (file.size > MAX_IMPORT_FILE_SIZE) {
            resetSelection();
            setError(FILE_TOO_LARGE_MESSAGE);
            return;
        }

        setError(null);
        setFileName(file.name);
        setSuccess(true);
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        validateAndSelectFile(file);
        e.target.value = "";
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        const file = e.dataTransfer.files[0];
        validateAndSelectFile(file);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setDragOver(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setDragOver(false);
    };

    const handleExportExample = (type) => {
        let content, mimeType, fileName;
        if (type === "json") {
            content = JSON.stringify(exampleJson, null, 2);
            mimeType = "application/json";
            fileName = "exemple_import.json";
        } else {
            content = exampleCsv;
            mimeType = "text/csv";
            fileName = "exemple_import.csv";
        }
        const blob = new Blob([content], { type: mimeType });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName;
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div className="space-y-8">
            {/* Section Import */}
            <div className="space-y-6">                
                {/* Zone de drop */}
                <div
                    className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-colors duration-200 ${
                        dragOver 
                            ? "border-[--color-primary] bg-[--bg-color]" 
                            : "border-gray-300 hover:border-gray-400"
                    }`}
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                >
                    <input
                        type="file"
                        accept=".json,.csv"
                        onChange={handleFileChange}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        aria-invalid={Boolean(error)}
                    />
                    <div className="space-y-4">
                        <div className="bg-gray-100 rounded-full p-4 mx-auto w-16 h-16 flex items-center justify-center">
                            <FaCloudUploadAlt className="text-gray-400 text-2xl" />
                        </div>
                        <div>
                            <p className="text-lg font-medium text-gray-900">
                                Glissez-déposez votre fichier ici
                            </p>
                            <p className="text-sm text-gray-500 mt-1">
                                ou <span className="text-[--color-primary] hover:text-[--color-primary] cursor-pointer">cliquez pour parcourir</span>
                            </p>
                        </div>
                        <p className="text-xs text-gray-400">
                            Formats acceptés: JSON, CSV (max 10 MB)
                        </p>
                    </div>
                </div>

                {error && (
                    <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
                        <FaExclamationCircle className="text-red-600 mt-0.5" size={16} />
                        <div>
                            <p className="font-medium text-red-900">Import refusé</p>
                            {error.split("\n").map(line => (
                                <p key={line} className="text-sm text-red-700">{line}</p>
                            ))}
                        </div>
                    </div>
                )}

                {/* Statut du fichier */}
                {fileName && (
                    <div className="flex items-center gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <FaCheck className="text-blue-600" size={16} />
                        <div>
                            <p className="font-medium text-blue-900">Fichier sélectionné</p>
                            <p className="text-sm text-blue-700">{fileName}</p>
                        </div>
                    </div>
                )}

                {success && (
                    <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
                        <FaCheck className="text-green-600" size={16} />
                        <div>
                            <p className="font-medium text-green-900">Fichier valide</p>
                            <p className="text-sm text-green-700">Le fichier respecte les contraintes d'import.</p>
                        </div>
                    </div>
                )}
            </div>

            {/* Section Export */}
            <div className="space-y-6">
                <div className="flex items-center gap-3 mb-6">
                    <div className="bg-green-100 rounded-full p-2">
                        <FaDownload className="text-green-600" size={16} />
                    </div>
                    <div>
                        <h4 className="text-lg font-semibold text-gray-800">Télécharger des exemples</h4>
                        <p className="text-sm text-gray-600">Obtenez des modèles pour structurer vos données</p>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="border border-gray-200 rounded-lg p-6 hover:border-gray-300 transition-colors duration-200">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="bg-purple-100 rounded-full p-2">
                                <FaFileCode className="text-purple-600" size={16} />
                            </div>
                            <div>
                                <h5 className="font-medium text-gray-900">Format JSON</h5>
                                <p className="text-sm text-gray-600">Structure de données JavaScript</p>
                            </div>
                        </div>
                        <button
                            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors duration-200"
                            onClick={() => handleExportExample("json")}
                        >
                            <FaDownload size={14} />
                            Télécharger JSON
                        </button>
                    </div>

                    <div className="border border-gray-200 rounded-lg p-6 hover:border-gray-300 transition-colors duration-200">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="bg-orange-100 rounded-full p-2">
                                <FaFileCsv className="text-orange-600" size={16} />
                            </div>
                            <div>
                                <h5 className="font-medium text-gray-900">Format CSV</h5>
                                <p className="text-sm text-gray-600">Valeurs séparées par des virgules</p>
                            </div>
                        </div>
                        <button
                            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors duration-200"
                            onClick={() => handleExportExample("csv")}
                        >
                            <FaDownload size={14} />
                            Télécharger CSV
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Import;
